import React from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";

/**
 * 14030 — Top bar for the Questionnaire & Risk Analysis tab.
 *
 * Layout (matches the design):
 *   [icon] Title             Last Publish by … : datetime  [Unpublish]
 *          Vendor · Location                       Vendor Completed —— 100%
 *                                                  100% Auto-scored — 0% SME-scored
 *
 * Publish metadata + Unpublish are stubbed for now (no publish workflow on
 * this ticket). Showing the metadata when the VQQ has a release date so the
 * card never renders an empty "Last Publish by null" row.
 *
 * The Auto-scored / SME-scored split is reviewer progress and renders only
 * when the caller shows a risk surface: never on a custom VQQ, and for a
 * sponsor only once an admin has published the run.
 */
const VqqPublishHeader = ({
  title,
  vendorLine,
  lastPublishBy,
  lastPublishAt,
  vendorCompletedPct,
  autoScoredPct,
  smeScoredPct,
  showScoringProgress = false,
}) => {
  return (
    <header className="rounded-md border border-gray-200 bg-white px-4 py-3">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-indigo-50 border border-indigo-100">
            <DocumentTextIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">{title}</h2>
            {vendorLine ? (
              <p className="text-xs text-gray-500 truncate">{vendorLine}</p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-1 min-w-0 justify-center">
          {lastPublishBy ? (
            <p className="text-xs italic text-gray-600 truncate">
              Last Publish by <span className="not-italic font-medium text-gray-700">{lastPublishBy}</span>
              {lastPublishAt ? `: ${formatStamp(lastPublishAt)}` : null}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <ProgressDual
            label="Vendor Completed"
            value={vendorCompletedPct}
            color="bg-emerald-500"
            barWidth="w-44"
          />
          {showScoringProgress ? (
            <DualSplit
              leftLabel={`${pct(autoScoredPct)}% Auto-scored`}
              rightLabel={`${pct(smeScoredPct)}% SME-scored`}
              leftPct={autoScoredPct}
            />
          ) : null}
        </div>
      </div>
    </header>
  );
};

const ProgressDual = ({ label, value, color, barWidth = "w-40" }) => {
  const v = pct(value);
  return (
    <div className="flex flex-col items-stretch">
      <div className="flex justify-between text-[10px] font-medium text-gray-600">
        <span>{label}</span>
        <span className="tabular-nums">{v}%</span>
      </div>
      <div className={`h-1.5 rounded-full bg-gray-200 overflow-hidden ${barWidth}`}>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

const DualSplit = ({ leftLabel, rightLabel, leftPct }) => {
  const v = pct(leftPct);
  return (
    <div className="flex flex-col items-stretch">
      <div className="flex justify-between text-[10px] font-medium text-gray-600 w-72">
        <span className="text-blue-700">{leftLabel}</span>
        <span className="text-violet-700">{rightLabel}</span>
      </div>
      <div className="h-1.5 rounded-full bg-violet-200 overflow-hidden w-72">
        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

const pct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
};

const formatStamp = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

export default VqqPublishHeader;
